import { useRef } from 'react';

import type { PlanAppointmentsRequest } from 'generated';

import { createPlanAppointmentsTerminalTracker } from '../planAppointmentsTerminal';
import type {
  HandlePlanAppointmentsOptions,
  PlanAppointmentsMutationCallbacks
} from '../utils';
import { handlePlanAppointments as handlePlanAppointmentsUtil } from '../utils';
import showError from 'components/shared/showError';
import type { FrozenDropHandle } from './useFrozenDrop';

/**
 * The loading state a view registers for one plan-appointments call — the Cards
 * view's per-column operation. Views whose overlay covers the whole board
 * register none.
 */
export interface PlanAppointmentsOperation {
  /** Releases the loading state registered for this call. */
  release: () => void;
  /**
   * False once a newer drag has started: this operation no longer owns the
   * shared drag-preview state and must not tear it down.
   */
  ownsDragPreview: () => boolean;
}

/**
 * The drop feedback of one call, handed to every callback of that call. Its
 * releases are keyed on the freeze and the operation this call created, so a
 * late callback can't tear down a newer drop's feedback.
 */
export interface PlanAppointmentsCall {
  /** Releases this call's loading state; no-op when the view registered none. */
  releaseOperation: () => void;
  /**
   * Whether this call still owns the shared drag-preview state. Always true in a
   * view that registers no operation: nothing there tracks the handover, and
   * those views tear the preview down unguarded.
   */
  ownsDragPreview: () => boolean;
  /**
   * Clears this call's frozen frame immediately. For cancel/error paths, where
   * the optimistic state is reverted and there is no incoming data to wait for.
   */
  releaseFrozenNow: () => void;
  /**
   * Clears this call's frozen frame once loading settles, so it covers the
   * refetch too and the card lands at its final position as the frame clears.
   */
  releaseFrozenWhenSettled: () => void;
}

interface UsePlanAppointmentsWithFrozenDropOptions {
  /**
   * The util options this hook passes straight through. It wires the rest
   * itself: the payload, the mutation, and every terminal callback.
   */
  util: Omit<
    HandlePlanAppointmentsOptions,
    | 'payload'
    | 'planAppointments'
    | 'onMutationError'
    | 'onCancelWarningDialog'
    | 'onSuccess'
    | 'onComplete'
  >;
  /**
   * Sends the mutation. `call` is there for a failure the util never hears
   * about — a throw on the way out — which has to release this call's feedback
   * itself.
   */
  planAppointments: (
    payload: PlanAppointmentsRequest,
    callbacks: PlanAppointmentsMutationCallbacks,
    call: PlanAppointmentsCall
  ) => void;
  /**
   * View side effects for a failed call; the releases and the error snackbar
   * are already handled.
   */
  onError?: (call: PlanAppointmentsCall) => void;
  /** View side effects for a cancelled warning dialog. */
  onCancel?: (call: PlanAppointmentsCall) => void;
  /** View side effects for a successful call. */
  onSuccess?: (call: PlanAppointmentsCall) => void;
  /**
   * View side effects for a call that is over, whatever the outcome — unlike the
   * releases in the same slot, which only run when nothing else reported it.
   */
  onComplete?: () => void;
}

interface UsePlanAppointmentsWithFrozenDropReturn {
  /**
   * Records the freeze a drop just created as the most recent one, so the
   * callbacks of the call it belongs to can release it — including the
   * recurring-confirmation path, which fires its mutation renders after the
   * drop. Call it with the handle `useFrozenDrop`'s `freeze` returned.
   *
   * The handle is kept here rather than in a ref of the view's own: writing to a
   * ref a hook returned bails the whole component out of the React Compiler.
   */
  registerFrozenDrop: (handle: FrozenDropHandle) => void;
  /**
   * The most recent freeze, or null when nothing is frozen. For the paths that
   * are not a plan-appointments call: releasing the newest freeze on an
   * unrelated error, or handing a specific drop's handle to a deferred unassign.
   * Its releases no-op once a newer drop owns the preview.
   */
  getFrozenDrop: () => FrozenDropHandle | null;
  /**
   * Sends one plan-appointments call and owns its drop feedback from the drop to
   * whichever callback ends up reporting the call. `startOperation` registers
   * the view's loading state for it, and is skipped along with the call itself
   * when there is no payload.
   */
  handlePlanAppointments: (
    payload: PlanAppointmentsRequest | null,
    startOperation?: () => PlanAppointmentsOperation | null
  ) => void;
}

/**
 * One drop lifecycle for the Pragmatic Timeline / Cards / Roster views: which
 * freeze (and which per-column operation) a plan-appointments call is
 * responsible for, and which callback releases it when.
 *
 * The views differ only in their side effects — what to reset, and whether the
 * reset is guarded on still owning the drag preview — so they pass those in and
 * this decides *whether* and *when* to release the drop feedback:
 *
 * - cancel/error clear the frozen frame right away: nothing is in flight after
 *   them, so there is no settle coming to wait for;
 * - success defers to the settle, so the frame covers the refetch too;
 * - `onComplete` is the last resort for a path that reports through none of the
 *   above (a `success: false` response carrying no planning entities), which
 *   would otherwise strand the feedback until the `useFrozenDrop` watchdog.
 *
 * See `createPlanAppointmentsTerminalTracker` for how "nothing else reported it"
 * is decided, and `useFrozenDrop` for the freeze itself.
 */
export const usePlanAppointmentsWithFrozenDrop = ({
  util,
  planAppointments,
  onError,
  onCancel,
  onSuccess,
  onComplete
}: UsePlanAppointmentsWithFrozenDropOptions): UsePlanAppointmentsWithFrozenDropReturn => {
  const pendingFrozenDropRef = useRef<FrozenDropHandle | null>(null);

  const handlePlanAppointments = (
    payload: PlanAppointmentsRequest | null,
    startOperation?: () => PlanAppointmentsOperation | null
  ) => {
    // The freeze this call is responsible for releasing: the one created by the
    // drop that led here, whether that was this tick or before the recurring
    // confirmation. Its releases no-op once a newer drop owns the preview.
    const frozen = pendingFrozenDropRef.current;

    // Without a payload the util fires no callback at all, so nothing would ever
    // report this call as finished: don't register an operation — its columns
    // would spin, and stay un-droppable, until the watchdog — and clear the
    // frozen frame here instead. The util still runs: it resets the confirmation
    // popover, which would otherwise stay open holding the frozen card.
    if (!payload) frozen?.releaseNow();

    const operation = payload ? startOperation?.() ?? null : null;

    const call: PlanAppointmentsCall = {
      releaseOperation: () => operation?.release(),
      ownsDragPreview: () => operation?.ownsDragPreview() ?? true,
      releaseFrozenNow: () => frozen?.releaseNow(),
      releaseFrozenWhenSettled: () => frozen?.releaseWhenSettled()
    };

    const terminal = createPlanAppointmentsTerminalTracker();
    // Last resort: a `success: false` response that carries no planning entities
    // opens no dialog and reaches none of the callbacks below, so `onComplete` is
    // the only signal that the call is over.
    const releaseIfUnaccounted = terminal.runIfNothingElseReported(() => {
      call.releaseOperation();
      call.releaseFrozenWhenSettled();
    });

    handlePlanAppointmentsUtil({
      ...util,
      payload,
      planAppointments: (planPayload, callbacks) =>
        planAppointments(planPayload, callbacks, call),
      setDialogState: terminal.trackDialogState(util.setDialogState),
      onMutationError: terminal.report((error) => {
        call.releaseOperation();
        // Released whatever the view makes of the failure, and outside any
        // `ownsDragPreview()` guard of its own: starting a newer drag invalidates
        // that, but only a *drop* creates a new freeze, so after an abandoned
        // drag this frame is still ours to clear. The handle no-ops once a newer
        // drop has taken the preview over.
        call.releaseFrozenNow();
        // A refused move (a 409 with a known backend code) gets its own message;
        // anything else the generic one. Owned here so every view gets it.
        showError(error);
        onError?.(call);
      }),
      onCancelWarningDialog: terminal.report(() => {
        // Nothing is in flight after a cancel, so clear the frozen frame right
        // away instead of waiting for a settle that isn't coming.
        call.releaseFrozenNow();
        call.releaseOperation();
        onCancel?.(call);
      }),
      onSuccess: terminal.report(() => {
        call.releaseOperation();
        // Deferred to the settle, so the frozen frame clears exactly when the
        // card snaps to its final position.
        call.releaseFrozenWhenSettled();
        onSuccess?.(call);
      }),
      onComplete: () => {
        // The view's side effects run whatever the outcome, unlike the
        // conditional release they share this slot with.
        onComplete?.();
        releaseIfUnaccounted();
      }
    });
  };

  return {
    registerFrozenDrop: (handle) => {
      pendingFrozenDropRef.current = handle;
    },
    getFrozenDrop: () => pendingFrozenDropRef.current,
    handlePlanAppointments
  };
};
